/* eslint-disable react/prop-types */
import React from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { connectedReduxRedirect } from 'redux-auth-wrapper/history4/redirect';
import { replace } from 'connected-react-router';
import AuthenticatedLayout from './components/auth/layouts/authenticated';
import NotAuthenticatedLayout from './components/auth/layouts/not-authenticated';
import AdminDetails from './components/admin/details';
import BatchUploadEdit from './components/batch/batch-upload-edit';
import EntityDetails from './components/entity/details';
import PortalDetails from './components/portal/details';
import PortalDashboard from './components/portal/dashboard';
import PublicLayout from './components/auth/layouts/public';
import NotFoundPage from './components/not-found-page';
import AdminConsole from './components/admin-console';
import NewDashboard from './components/new-dashboard';
import Details from './components/details';
import Calendar from './components/calendar';
import InvalidAuth from './components/auth/invalid';
import Login from './components/auth/login';
import TokenAuth from './components/auth/token-auth';
import Logout from './components/auth/logout';
import PasswordRecovery from './components/auth/password-recovery';
import Map from './components/map';
import PrintMap from './components/print-map';
import Cycle from './components/workflow/cycle';
import OverlapDetail from './components/overlap';
import NewDashboardDataTable from './components/new-dashboard/data-table';
import Portal from '@components/portal';
import Settings from './components/settings';
import SettingsProfile from './components/settings/profile';
import SettingsEmail from './components/settings/email';
import SettingsEmailDetails from './components/settings/email/details';
import SettingsAdminConsole from './components/settings/admin-console';
import SettingsBatch from './components/settings/batch';
import SettingsDashboard from './components/settings/dashboard';
import TimeLayout from './components/settings/time';
import SettingsFilePreview from './components/settings/file-preview';
import SettingsNotifications from './components/settings/notifications';
import DashboardPortal from './components/dashboardportal';
import ActivityLogs from './components/activity-logs';
// import Group from './components/group';
import GroupDetails from './components/group/details';
import Gantt from './components/group/gantt';
import PublicDetails from './components/public/details';
import PublicDetailsSuccess from './components/public/success';
import { isPCCO, getAgencyId } from '@utils/permission-utils';
import { withRouter } from '@utils/router-utils';
import ComponentZoo from '@components/dev/zoo';
import { AUTH_LOGIN_ROUTE } from '@constants/auth';
import { authSelector } from '@utils/auth-utils';

// Redirects to /login by default:
const userIsAuthenticated = connectedReduxRedirect({
  // Check authentication state but also pass if a unexpired token exists in cookies
  authenticatedSelector: state => state.auth.valid || authSelector(),
  redirectPath: AUTH_LOGIN_ROUTE,
  redirectAction: replace,
  wrapperDisplayName: 'UserIsAuthenticated'
});

const Authenticated = withRouter(userIsAuthenticated(props => props.children));

// Routes for authenticated users:
const ProtectedRoute = ({ component: Component, ...other }) => (
  <Route {...other} render={props => (
    <Authenticated {...props}>
      <AuthenticatedLayout {...props}>
        <Component {...props} />
      </AuthenticatedLayout>
    </Authenticated>
  )} />
);

const PortalDashboardRoute = ({ component: Component, ...other }) => (
  <Route {...other} render={props => (
    <Authenticated {...props}>
      <AuthenticatedLayout {...props}>
        <PortalDashboard {...props}>
          <Component {...props} />
        </PortalDashboard>
      </AuthenticatedLayout>
    </Authenticated>
  )} />
);

const NewDashboardRoute = ({ component: Component, ...other }) => (
  <Route {...other} render={props => (
    <Authenticated {...props}>
      <AuthenticatedLayout {...props}>
        <NewDashboard {...props}>
          <Component {...props} />
        </NewDashboard>
      </AuthenticatedLayout>
    </Authenticated>
  )} />
);

// Wrapper for People dashboard routes (new dashboard) - non-PCCO users trying to access
// agency listing get redirected to their own agency details page.
const PeopleDashboardRoute = ({ component: Component, ...other }) => (
  <Route {...other} render={props => {
    // Check if non-PCCO user is trying to access agency listing
    const { dataType } = props.match.params;
    if (dataType === 'agency' && !isPCCO()) {
      return <Redirect to={`/agency/${getAgencyId()}`} />;
    }
    return (
      <Authenticated {...props}>
        <AuthenticatedLayout {...props}>
          <NewDashboard {...props}>
            <Component {...props} />
          </NewDashboard>
        </AuthenticatedLayout>
      </Authenticated>
    );
  }} />
);

// This route is for the agency listing page (old dashboard), if the user has no permissions
// to see all agencies, he'll be redirected to the agency details URL.
// It would be a better solution if we don't display the "Agencies" menu option
// for these users, display a "Agency" option instead.
const AgencyDashboardRoute = ({ component: Component, ...other }) => {
  const newOther = { ...other };
  let NewComponent = Component;
  if (!isPCCO()) {
    // Non-PCCO users must not see the agency listing page, thus
    // display the user's agency details page.
    newOther.computedMatch.params = { dataType: 'agency', dataId: getAgencyId() };
    newOther.computedMatch.path = '/agency/:id/:edit?';
    newOther.path = '/agency/:id/:edit?';
    NewComponent = PortalDetails;
  } else {
    newOther.computedMatch.params = { dataType: 'agency' };
    newOther.computedMatch.path = '/people/:dataType(agency)';
    newOther.path = '/people/:dataType(agency)';
  }

  return (
    <Route {...newOther} render={props => (
      <Authenticated {...props}>
        <AuthenticatedLayout {...props}>
          <Portal {...props}>
            <NewComponent {...props} />
          </Portal>
        </AuthenticatedLayout>
      </Authenticated>
    )} />
  );
};

const DetailsRoute = ({ component: Component, ...other }) => (
  <Route {...other} render={props => (
    <Authenticated {...props}>
      <AuthenticatedLayout {...props}>
        <Details {...props}>
          <Component {...props} />
        </Details>
      </AuthenticatedLayout>
    </Authenticated>
  )} />
);

const EmbeddedDetailsRoute = ({ component: Component, ...other }) => (
  <Route {...other} render={props => (
    <Authenticated {...props}>
      <AuthenticatedLayout {...props}>
        <NewDashboard {...props}>
          <Details {...props}>
            <Component {...props} />
          </Details>
        </NewDashboard>
      </AuthenticatedLayout>
    </Authenticated>
  )} />
);

const AdminConsoleRoute = ({ component: Component, ...other }) => (
  <Route {...other} render={props => (
    <Authenticated {...props}>
      <AuthenticatedLayout {...props}>
        <AdminConsole {...props}>
          <Component {...props} />
        </AdminConsole>
      </AuthenticatedLayout>
    </Authenticated>
  )} />
);

const AdminConsoleEmbeddedDetailsRoute = ({ component: Component, ...other }) => (
  <Route {...other} render={props => (
    <Authenticated {...props}>
      <AuthenticatedLayout {...props}>
        <AdminConsole {...props}>
          <Details {...props}>
            <Component {...props} />
          </Details>
        </AdminConsole>
      </AuthenticatedLayout>
    </Authenticated>
  )} />
);

const SettingsRoute = ({ component: Component, ...other }) => (
  <Route {...other} render={props => (
    <Authenticated {...props}>
      <AuthenticatedLayout {...props}>
        <Settings {...props}>
          <Component {...props} />
        </Settings>
      </AuthenticatedLayout>
    </Authenticated>
  )} />
);

// Routes that don't require authentication (the only difference between them is the layout component):
const OpenRoute = ({ component: Component, ...other }) => (
  <Route {...other} render={props => (
    <NotAuthenticatedLayout {...props}>
      <Component {...props} />
    </NotAuthenticatedLayout>
  )} />
);

const PublicRoute = ({ component: Component, ...other }) => (
  <Route {...other} render={props => (
    <PublicLayout {...props}>
      <Component {...props} />
    </PublicLayout>
  )} />
);

const Routes = ({ defaultUrl, useNewAdminConsole, useNewDashboard }) => (
  <Switch>
    <Redirect exact from="/" to={defaultUrl} />
    <Redirect exact from="/list" to="/library" />
    <Redirect exact from="/workflow" to="/workflow/activity/owner" />
    <Redirect exact from="/admin" to="/admin/data/workflow" />
    <Redirect exact from="/admin/:dataType" to="/admin/data/:dataType" />
    <Redirect exact from="/overlap/:subType/:dataId" to="/overlap/:dataId" />

    <PublicRoute path="/publicform/:dataType/success" component={PublicDetailsSuccess} />
    <PublicRoute path="/publicform/:dataType" component={PublicDetails} />

    <OpenRoute path="/auth/invalid" component={InvalidAuth} />
    <OpenRoute path="/auth/:token?/:userId?/:timestamp?" component={TokenAuth} />
    <OpenRoute path="/login" component={Login} />
    <OpenRoute path="/logout" component={Logout} />
    <OpenRoute path="/password/:userPk?/:token?/:onboarding?" component={PasswordRecovery} />

    <ProtectedRoute path="/calendar" component={Calendar} />
    <ProtectedRoute path="/:dataType(cycle)/:id/task/new/:taskTypeId" component={Cycle} />
    <ProtectedRoute path="/:dataType(cycle)/:id/task/:taskId/activity/:taskActivityId" component={Cycle} />
    <ProtectedRoute path="/:dataType(cycle)/:id/task/:taskId" component={Cycle} />
    <ProtectedRoute path="/:dataType(cycle)/:id" component={Cycle} />
    <ProtectedRoute path="/group/gantt/:groupType/:dataId" component={Gantt} />
    <DetailsRoute path="/:dataType(group)/:groupType/:id" component={GroupDetails} />
    <ProtectedRoute path="/logs/:app/:model/:dataId" component={ActivityLogs} />
    <ProtectedRoute path="/map/print" component={PrintMap} />
    <ProtectedRoute path="/map" component={Map} />
    <ProtectedRoute path="/overlap/:dataId" component={OverlapDetail} />
    {/* Library routes - conditional based on dashboard setting */}
    {useNewDashboard && <NewDashboardRoute path="/library/:dataType?/:subType?" component={NewDashboardDataTable} />}
    {!useNewDashboard && <ProtectedRoute path="/library/:dataType?/:subType?" component={Portal} />}

    {process.env.NODE_ENV === 'development' &&
      <ProtectedRoute path="/dev/zoo" component={ComponentZoo} />
    }

    {/* Workflow routes - conditional based on dashboard setting */}
    {useNewDashboard && <NewDashboardRoute path="/workflow/:dataType?/:subType?" component={NewDashboardDataTable} />}
    {!useNewDashboard && <ProtectedRoute path="/workflow/:dataType?/:subType?" component={Portal} />}

    {/* People routes - conditional based on dashboard setting */}
    {useNewDashboard && <PeopleDashboardRoute path="/people/data/:dataType" component={NewDashboardDataTable} />}
    {useNewDashboard && <Route path="/people/:dataType(agency)" render={() => <Redirect to="/people/data/agency" />} />}
    {useNewDashboard && <Route path="/people/:dataType(user)" render={() => <Redirect to="/people/data/user" />} />}
    {!useNewDashboard && <AgencyDashboardRoute path="/people/:dataType(agency)" component={Portal} />}
    {!useNewDashboard && <ProtectedRoute path="/people/:dataType?/:subType?" component={Portal} />}

    <ProtectedRoute path="/:dataType(batch-upload-edit)/:id/:rowId?" component={BatchUploadEdit} />
    <PortalDashboardRoute path="/:dataType(agency)/:id/:edit?" component={PortalDetails} />
    <PortalDashboardRoute path="/:dataType(batch)/:id" component={PortalDetails} />
    <PortalDashboardRoute path="/:dataType(user)/:id/:edit?" component={PortalDetails} />
    {useNewAdminConsole && <AdminConsoleRoute path="/admin/data/:dataType" component={NewDashboardDataTable} />}
    {!useNewAdminConsole && <NewDashboardRoute path="/admin/data/:dataType" component={NewDashboardDataTable} />}
    {useNewAdminConsole && <AdminConsoleEmbeddedDetailsRoute path="/admin/:dataType/:id" component={AdminDetails} />}
    {!useNewAdminConsole && <EmbeddedDetailsRoute path="/admin/:dataType/:id" component={AdminDetails} />}

    <SettingsRoute path="/settings/profile" component={SettingsProfile} />
    <SettingsRoute path="/settings/email/:type" component={SettingsEmailDetails} />
    <SettingsRoute path="/settings/email" component={SettingsEmail} />
    <SettingsRoute path="/settings/notifications" component={SettingsNotifications} />
    <SettingsRoute path="/settings/admin-console" component={SettingsAdminConsole} />
    <SettingsRoute path="/settings/batch" component={SettingsBatch} />
    <SettingsRoute path="/settings/dashboard" component={SettingsDashboard} />
    <SettingsRoute path="/settings/time" component={TimeLayout} />
    <SettingsRoute path="/settings/file-preview" component={SettingsFilePreview} />

    <ProtectedRoute path="/:portalType(dashboard)/" component={DashboardPortal} />
    {/* We can use the DashboardPortal component to build different types of dashboards: */}
    {/* <ProtectedRoute path="/:portalType(budgeting)/" component={DashboardPortal} /> */}

    <DetailsRoute path="/:dataType/new" component={EntityDetails} />
    <DetailsRoute path="/:dataType/:id/:action" component={EntityDetails} />
    <DetailsRoute path="/:dataType/:id" component={EntityDetails} />

    <Route path="*" component={NotFoundPage} />
  </Switch>
);

const RoutesWrapper = () => {
  const { defaultUrl, global } = useSelector(state => state.config);
  const useNewDashboard = global?.dashboard === 'new';
  const useNewAdminConsole = global?.admin_console === 'default';
  return <Routes defaultUrl={defaultUrl} useNewAdminConsole={useNewAdminConsole} useNewDashboard={useNewDashboard} />;
};

export default RoutesWrapper;
